import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, ExternalLink, Loader2, Mail, Radar, Swords } from "lucide-react";

export function CompetitorWatchPanel({ siteId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [checking, setChecking] = useState(false);
  const [riposting, setRiposting] = useState(new Set());
  const [langs, setLangs] = useState({ default_language: "fr", available_languages: [] });
  const [pickerUrl, setPickerUrl] = useState(null);
  const [pickerSel, setPickerSel] = useState(new Set());
  const [watchEmail, setWatchEmail] = useState("");
  const [savedEmail, setSavedEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const pollRef = useRef(null);

  const guessLang = (url) => {
    const m = url.match(/[/.](fr|de|en|it)(?:[/.-]|$)/i);
    return m ? m[1].toLowerCase() : null;
  };

  const load = useCallback(async () => {
    if (!siteId) return;
    try {
      const { data } = await api.get(`/sites/${siteId}/competitor-watch`);
      setItems(data?.competitors || []);
    } catch {
      setItems([]);
    }
    try {
      const { data } = await api.get(`/sites/${siteId}/languages`);
      setLangs(data || { default_language: "fr", available_languages: [] });
    } catch { /* langues optionnelles */ }
    try {
      const { data } = await api.get(`/sites/${siteId}`);
      setWatchEmail(data?.watch_email || "");
      setSavedEmail(data?.watch_email || "");
    } catch { /* destinataire optionnel */ }
  }, [siteId]);

  const saveWatchEmail = async () => {
    setSavingEmail(true);
    try {
      const { data } = await api.patch(`/sites/${siteId}`, { watch_email: watchEmail.trim() });
      setWatchEmail(data?.watch_email || "");
      setSavedEmail(data?.watch_email || "");
      toast.success(data?.watch_email
        ? `Alertes de veille envoyées à ${data.watch_email}`
        : "Alertes de veille envoyées à l'adresse du compte");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'enregistrer le destinataire.");
    } finally {
      setSavingEmail(false);
    }
  };

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, [load]);

  const totalNew = items.reduce((s, it) => s + (it.new_urls || []).length, 0);
  const lastChecked = items.length ? items.map((i) => i.last_checked).filter(Boolean).sort().slice(-1)[0] : null;

  const runNow = async (e) => {
    e.stopPropagation();
    setChecking(true);
    try {
      const { data } = await api.post(`/sites/${siteId}/competitor-watch/run`);
      toast.info("Vérification des sites concurrents en cours…");
      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts += 1;
        if (attempts > 40) {
          clearInterval(pollRef.current);
          setChecking(false);
          toast.error("La vérification prend trop de temps. Réessayez.");
          return;
        }
        try {
          const { data: job } = await api.get(`/content/jobs/${data.job_id}`);
          if (job.status === "completed") {
            clearInterval(pollRef.current);
            setChecking(false);
            setOpen(true);
            load();
            const n = job.result?.total_new || 0;
            const baseline = (job.result?.competitors || []).some((c) => c.baseline);
            if (n > 0) toast.success(`${n} nouvelle(s) page(s) détectée(s) chez vos concurrents !`);
            else if (baseline) toast.success("Point de départ enregistré — les prochaines vérifications signaleront les nouvelles pages.");
            else toast.success("Rien de nouveau chez vos concurrents.");
          } else if (job.status === "failed") {
            clearInterval(pollRef.current);
            setChecking(false);
            toast.error(job.error || "Vérification échouée.");
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (err) {
      setChecking(false);
      toast.error(err?.response?.data?.detail || "Impossible de lancer la vérification.");
    }
  };

  const riposte = async (url, competitorName, locales = []) => {
    setPickerUrl(null);
    setRiposting((s) => new Set(s).add(url));
    const done = () => setRiposting((s) => { const n = new Set(s); n.delete(url); return n; });
    try {
      const { data } = await api.post(`/sites/${siteId}/competitor-riposte`, {
        url, competitor_name: competitorName, locales,
      });
      const nbLang = Math.max(1, locales.length || 1);
      toast.info(nbLang > 1
        ? `Riposte multilingue lancée (${locales.map((l) => l.toUpperCase()).join(", ")}) — génération en cours (2-5 min)…`
        : "Riposte lancée — lecture de la page concurrente puis génération du brouillon (1-2 min)…");
      let attempts = 0;
      const interval = setInterval(async () => {
        attempts += 1;
        if (attempts > 90) { clearInterval(interval); done(); toast.error("La riposte prend trop de temps. Réessayez."); return; }
        try {
          const { data: job } = await api.get(`/content/jobs/${data.job_id}`);
          if (job.status === "completed") {
            clearInterval(interval);
            done();
            const gen = job.result?.locales_generated || [];
            const errs = job.result?.locale_errors || {};
            toast.success(gen.length > 0
              ? `Riposte « ${job.result?.title || ""} » prête + versions ${gen.map((l) => l.toUpperCase()).join(", ")} — voir Contenu multilingue !`
              : `Brouillon riposte « ${job.result?.title || ""} » prêt — retrouvez-le dans Brouillons !`);
            const failed = Object.keys(errs);
            if (failed.length > 0) {
              toast.warning(`Langue(s) en échec : ${failed.map((l) => `${l.toUpperCase()} (${errs[l]})`).join(" · ")}`);
            }
          } else if (job.status === "failed") {
            clearInterval(interval);
            done();
            toast.error(job.error || "Riposte échouée.");
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (err) {
      done();
      toast.error(err?.response?.data?.detail || "Impossible de lancer la riposte.");
    }
  };

  const openPicker = (url) => {
    const sel = new Set([langs.default_language]);
    const guessed = guessLang(url);
    if (guessed && langs.available_languages.some((l) => l.split("-")[0] === guessed)) {
      sel.add(langs.available_languages.find((l) => l.split("-")[0] === guessed));
    }
    setPickerSel(sel);
    setPickerUrl(url);
  };

  return (
    <div className="border border-slate-200 bg-white rounded-lg mb-6" data-testid="competitor-watch-panel">
      <div
        className="w-full flex items-center justify-between px-5 py-3.5 cursor-pointer flex-wrap gap-2"
        onClick={() => setOpen(!open)}
        data-testid="watch-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-900 flex-wrap">
          <Radar className="w-4 h-4 text-[#002FA7]" /> Veille concurrentielle
          {totalNew > 0 && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300" data-testid="watch-new-badge">
              {totalNew} nouveauté{totalNew > 1 ? "s" : ""}
            </span>
          )}
          <span className="text-xs font-normal text-slate-500">
            — automatique chaque lundi + notification
            {lastChecked ? ` · dernier contrôle : ${new Date(lastChecked).toLocaleDateString("fr-FR")}` : ""}
          </span>
        </span>
        <span className="flex items-center gap-3">
          <button
            onClick={runNow}
            disabled={checking}
            data-testid="watch-run-button"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#002FA7] border border-[#002FA7]/30 rounded-md px-3 py-1.5 hover:bg-blue-50 disabled:opacity-60"
          >
            {checking ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Vérification…</>
            ) : (
              "Vérifier maintenant"
            )}
          </button>
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </span>
      </div>
      {open && (
        <div className="px-5 pb-4">
          <div className="flex items-center gap-2 flex-wrap mb-3 text-xs" data-testid="watch-email-row">
            <Mail className="w-3.5 h-3.5 text-slate-500" />
            <label htmlFor={`watch-email-${siteId}`} className="font-medium text-slate-700">
              Destinataire des alertes e-mail
            </label>
            <input
              id={`watch-email-${siteId}`}
              type="email"
              value={watchEmail}
              onChange={(e) => setWatchEmail(e.target.value)}
              placeholder="adresse du compte (par défaut)"
              data-testid="watch-email-input"
              className="border border-slate-300 rounded-md px-2 py-1 text-xs w-64 focus:outline-none focus:border-[#002FA7]"
            />
            <button
              onClick={saveWatchEmail}
              disabled={savingEmail || watchEmail.trim() === savedEmail}
              data-testid="watch-email-save"
              className="text-xs font-medium text-white bg-[#002FA7] hover:bg-[#00237d] rounded-md px-3 py-1 disabled:opacity-50"
            >
              {savingEmail ? "Enregistrement…" : "Enregistrer"}
            </button>
            {savedEmail && (
              <span className="text-slate-500" data-testid="watch-email-current">· actuellement : {savedEmail}</span>
            )}
          </div>
          {items.length === 0 ? (
            <p className="text-xs text-slate-500" data-testid="watch-empty">
              Aucune vérification pour l'instant — cliquez sur « Vérifier maintenant » pour enregistrer le point de départ.
              Ensuite, chaque lundi, les nouvelles pages publiées par vos concurrents vous seront signalées ici,
              par notification et par e-mail.
            </p>
          ) : (
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={it.domain} className="border border-slate-200 rounded-md px-3 py-2" data-testid={`watch-item-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="font-semibold text-slate-900">{it.name}</span>
                    <span className="text-slate-400">· {it.domain}</span>
                    <span className="text-slate-500">{it.urls_count || 0} pages suivies</span>
                    {it.last_error && <span className="text-red-600">{it.last_error}</span>}
                    <span className="text-slate-400 ml-auto">
                      {it.last_checked ? new Date(it.last_checked).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : ""}
                    </span>
                  </div>
                  {(it.new_urls || []).length > 0 && (
                    <div className="mt-1.5 space-y-0.5">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                        Nouvelles pages depuis le contrôle précédent
                      </div>
                      {it.new_urls.map((u, j) => (
                        <div key={u}>
                          <div className="flex items-center gap-2">
                            <a href={u} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[11px] text-[#002FA7] hover:underline truncate">
                              <ExternalLink className="w-3 h-3 flex-shrink-0" /> {u}
                            </a>
                            <button
                              onClick={() => (langs.available_languages.length > 1 ? openPicker(u) : riposte(u, it.name))}
                              disabled={riposting.has(u)}
                              data-testid={`riposte-button-${i}-${j}`}
                              title="Générer un brouillon supérieur sur le même sujet (consomme 1 génération par langue)"
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 border border-red-200 bg-red-50 rounded-md px-2 py-0.5 hover:bg-red-100 disabled:opacity-60 flex-shrink-0"
                            >
                              {riposting.has(u) ? (
                                <><Loader2 className="w-3 h-3 animate-spin" /> Riposte…</>
                              ) : (
                                <><Swords className="w-3 h-3" /> Riposter</>
                              )}
                            </button>
                          </div>
                          {pickerUrl === u && (
                            <div className="mt-1.5 ml-4 flex items-center gap-2 flex-wrap border border-slate-200 bg-slate-50 rounded-md px-2.5 py-1.5" data-testid="riposte-lang-picker">
                              <span className="text-[10px] font-semibold text-slate-600">Langues de la riposte :</span>
                              {langs.available_languages.map((l) => {
                                const isDefault = l === langs.default_language;
                                const checked = pickerSel.has(l);
                                return (
                                  <label key={l} className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border cursor-pointer ${
                                    checked ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-slate-600 border-slate-300"
                                  } ${isDefault ? "opacity-80" : ""}`}>
                                    <input
                                      type="checkbox"
                                      className="hidden"
                                      checked={checked}
                                      disabled={isDefault}
                                      data-testid={`riposte-lang-${l}`}
                                      onChange={() => setPickerSel((prev) => {
                                        const n = new Set(prev);
                                        if (n.has(l)) n.delete(l); else n.add(l);
                                        return n;
                                      })}
                                    />
                                    {checked ? "✓" : "○"} {l.toUpperCase()}{isDefault ? " (défaut)" : ""}
                                  </label>
                                );
                              })}
                              <button
                                onClick={() => riposte(u, it.name, [...pickerSel])}
                                data-testid="riposte-launch-button"
                                className="text-[10px] font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md px-2.5 py-1"
                              >
                                Lancer ({pickerSel.size} génération{pickerSel.size > 1 ? "s" : ""})
                              </button>
                              <button
                                onClick={() => setPickerUrl(null)}
                                data-testid="riposte-cancel-button"
                                className="text-[10px] text-slate-500 hover:text-slate-700"
                              >
                                Annuler
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
